import { useEffect, useState } from 'react';
import Raven from 'raven-js';
import { useQuery } from '@apollo/client';
import { FETCH_INVOICE } from '../graphQL/invoiceHighlightQuery';
import { parseInvoice } from '../graphQL/invoiceHighlightQueryParser';
import InvoiceHighlightDetails from './InvoiceHighlightDetails';
import InvoiceHighlightActions from './InvoiceHighlightActions';
import NonNativeInvoiceHighlight from './NonNativeInvoiceHighlight';
import { LoadingSpinner } from 'foundations-components/transitional/components/LoadingSpinner';
import { getInvoiceProperty } from 'invoices-ui-lib/utils/property';
import { getPaymentStatus } from '../api/invoiceAPI';
import { getInvoiceSettings } from 'invoices-ui-lib/api/SettingsAPI';
import { isAccountApprovedForPayments } from 'invoices-ui-lib/utils/paymentsEligibility';
import { PaymentsEligibilityContext } from 'invoices-ui-lib/context/PaymentsEligibilityContext';
import { isOpen } from 'invoices-ui-lib/utils/status';
import NonNativeInvoiceHighlightActions from './NonNativeInvoiceHighlightActions';
import { useSetTrackerPaymentProperties } from 'invoices-ui-lib/hooks/useSetTrackerPaymentProperties';
import usageTracker from '../utils/usageTracker';
import { View } from 'foundations-components/transitional/components/View';
import { jsx as _jsx } from "react/jsx-runtime";
import { jsxs as _jsxs } from "react/jsx-runtime";
const getIsNativeInvoice = invoice => getInvoiceProperty(invoice, 'hs_invoice_source') === 'native_invoice';
export const InvoiceHighlightContainer = ({
  children,
  testId
}) => /*#__PURE__*/_jsx(View, {
  "data-test-id": testId,
  borderBlockEndWidth: "100",
  borderBlockEndStyle: "solid",
  borderBlockEndColor: "border-core-default",
  paddingInline: "600",
  paddingBlockStart: "600",
  children: children
});
export const HighlightLoadingSpinner = ({
  testIdSelector
}) => /*#__PURE__*/_jsx(InvoiceHighlightContainer, {
  testId: testIdSelector,
  children: /*#__PURE__*/_jsx(LoadingSpinner, {
    grow: true,
    minHeight: 128
  })
});
const InvoiceHighlight = props => {
  useSetTrackerPaymentProperties(usageTracker);
  const {
    objectId,
    permissions,
    closeSidebar,
    onObjectCreate,
    updatePreview
  } = props;
  const [paymentStatus, setPaymentStatus] = useState(undefined);
  const [canEditOpenInvoice, setCanEditOpenInvoice] = useState(false);
  const [canDeleteInvoice, setCanDeleteInvoice] = useState(false);
  const [allowEmailReminders, setAllowEmailReminders] = useState(false);
  const [pollInterval, setPollInterval] = useState(0);
  const {
    data
  } = useQuery(FETCH_INVOICE, {
    variables: {
      invoiceId: objectId
    },
    fetchPolicy: 'network-only',
    onError: apolloError => {
      Raven.captureException(apolloError);
    },
    pollInterval
  });
  const paymentsEligibility = data === null || data === void 0 ? void 0 : data.paymentsEligibility;
  const invoice = data ? parseInvoice(data.crmObject) : null;
  const isNativeInvoice = invoice ? getIsNativeInvoice(invoice) : false;
  const isOpenStatus = invoice ? isOpen(invoice) : false;
  const invoicePropertyDefinitions = data ? Object.values(data.crmObject.allProperties).map(property => property.definition).filter(property => Boolean(property)) : null;
  const accountApprovedForPayments = !!paymentsEligibility && isAccountApprovedForPayments(paymentsEligibility);
  useEffect(() => {
    if (isNativeInvoice && isOpenStatus) {
      getPaymentStatus(objectId).then(setPaymentStatus).catch(error => {
        Raven.captureException(error);
      });
    }
  }, [isNativeInvoice, isOpenStatus, objectId]);
  useEffect(() => {
    getInvoiceSettings().then(settings => {
      setCanEditOpenInvoice(settings.canEditOpenInvoice);
      setCanDeleteInvoice(settings.canDeleteInvoice);
      setAllowEmailReminders(settings.allowEmailReminders);
    }).catch(() => {});
  }, []);
  if (!invoice || !invoicePropertyDefinitions) {
    return /*#__PURE__*/_jsx(HighlightLoadingSpinner, {
      testIdSelector: "invoice-loader"
    });
  }
  if (!isNativeInvoice) {
    return /*#__PURE__*/_jsxs(InvoiceHighlightContainer, {
      children: [/*#__PURE__*/_jsx(NonNativeInvoiceHighlightActions, {
        invoice: invoice,
        hasInvoiceEditPermissions: permissions.canEditSubject,
        closeSidebar: closeSidebar,
        paymentStatus: paymentStatus,
        canDeleteInvoice: canDeleteInvoice
      }), /*#__PURE__*/_jsx(NonNativeInvoiceHighlight, {
        invoice: invoice,
        propertyDefinitions: invoicePropertyDefinitions
      })]
    });
  }
  if (isOpenStatus && !paymentStatus) {
    return /*#__PURE__*/_jsx(HighlightLoadingSpinner, {
      testIdSelector: "payment-status-loader"
    });
  }
  return /*#__PURE__*/_jsx(InvoiceHighlightContainer, {
    children: /*#__PURE__*/_jsxs(PaymentsEligibilityContext.Provider, {
      value: {
        paymentsEligibility
      },
      children: [/*#__PURE__*/_jsx(InvoiceHighlightActions, {
        invoice: invoice,
        hasInvoiceEditPermissions: permissions.canEditSubject,
        hasInvoiceDeletePermissions: permissions.canDeleteSubject,
        closeSidebar: closeSidebar,
        paymentStatus: paymentStatus,
        onObjectCreate: onObjectCreate,
        updatePreview: updatePreview,
        canEditOpenInvoice: canEditOpenInvoice,
        canDeleteInvoice: canDeleteInvoice,
        canEditReminders: accountApprovedForPayments && allowEmailReminders,
        gates: permissions.gates,
        scopes: permissions.scopes,
        setPollInterval: setPollInterval
      }), /*#__PURE__*/_jsx(InvoiceHighlightDetails, {
        invoice: invoice,
        propertyDefinitions: invoicePropertyDefinitions,
        allowEmailReminders: allowEmailReminders,
        accountApprovedForPayments: accountApprovedForPayments,
        isPollingForNextReminderDate: pollInterval > 0
      })]
    })
  });
};
export default InvoiceHighlight;